import type { ComponentKind, GroupId } from './types';

/**
 * 部件目录 —— 字段定义的**唯一事实来源**。
 *
 * 表单渲染、缺失参数提示、计算引擎读取字段，全部走这里，避免同一套字段在
 * 三处各写一遍后互相漂移。
 */

export interface GroupSpec {
  id: GroupId;
  label: string;
  description: string;
}

export const GROUP_SPECS: readonly GroupSpec[] = [
  { id: 'airframe', label: '机体', description: '机架等结构件' },
  { id: 'power', label: '动力组', description: '电机 / 电调 / 螺旋桨' },
  { id: 'energy', label: '能源', description: '电池' },
  { id: 'avionics', label: '航电', description: '飞控 / 图传 / 接收机' },
  { id: 'payload', label: '载荷', description: '相机 / 云台 / 任务设备' },
  { id: 'custom', label: '自定义', description: '用户自行添加的部件' },
];

export function groupLabel(id: GroupId): string {
  return GROUP_SPECS.find((group) => group.id === id)?.label ?? id;
}

export interface FieldSpec {
  key: string;
  /** 表单里的短标签 */
  label: string;
  /** 缺失提示里用的完整参数名；省略时由 `missingLabel` 自动拼装 */
  fullLabel?: string;
  unit?: string;
  kind: 'number' | 'text';
  placeholder?: string;
  hint?: string;
  min?: number;
  max?: number;
  step?: number;
  /** 只用于展示口径说明、不参与计算的字段 */
  informational?: boolean;
}

export interface ComponentSpec {
  kind: ComponentKind;
  label: string;
  group: GroupId;
  /** 4 轴默认数量 */
  defaultQty: number;
  /** 数量是否可改（动力组允许改，例如同轴双桨） */
  quantityEditable: boolean;
  /** 是否允许自定义显示名 */
  labelEditable: boolean;
  /** 该部件是否属于「动力四件套」，用于数量一致性校验 */
  driveTrain?: boolean;
  fields: readonly FieldSpec[];
}

/** 通用字段 */
const WEIGHT: FieldSpec = {
  key: 'weight_g',
  label: '重量',
  unit: 'g',
  kind: 'number',
  min: 0,
  step: 0.1,
  placeholder: '如 32',
};

const PRICE: FieldSpec = {
  key: 'price',
  label: '价格',
  unit: '¥',
  kind: 'number',
  min: 0,
  step: 1,
  placeholder: '如 89',
};

export const COMPONENT_SPECS: readonly ComponentSpec[] = [
  {
    kind: 'frame',
    label: '机架',
    group: 'airframe',
    defaultQty: 1,
    quantityEditable: false,
    labelEditable: true,
    fields: [WEIGHT, PRICE],
  },
  {
    kind: 'motor',
    label: '电机',
    group: 'power',
    defaultQty: 4,
    quantityEditable: true,
    labelEditable: true,
    driveTrain: true,
    fields: [
      { ...WEIGHT, label: '单重', hint: '单个电机的重量' },
      {
        key: 'thrust_g',
        label: '最大推力',
        fullLabel: '电机最大推力',
        unit: 'g',
        kind: 'number',
        min: 0,
        step: 10,
        placeholder: '实测单电机最大拉力',
        hint: '填写实测值最准确；留空则尝试按 KV / 桨径 / 螺距 / 电芯数经验估算',
      },
      { ...PRICE, label: '单价' },
      { key: 'kv', label: 'KV', fullLabel: '电机 KV', unit: 'KV', kind: 'number', min: 0, step: 10, placeholder: '如 920' },
      { key: 'max_current_a', label: '最大电流', fullLabel: '电机最大电流', unit: 'A', kind: 'number', min: 0, step: 1, placeholder: '如 30' },
      {
        key: 'efficiency_g_per_w',
        label: '效率',
        fullLabel: '电机效率',
        unit: 'g/W',
        kind: 'number',
        min: 0,
        step: 0.1,
        placeholder: '如 7.5',
        hint: '悬停效率，用于估算悬停功率与续航',
      },
    ],
  },
  {
    kind: 'esc',
    label: '电调',
    group: 'power',
    defaultQty: 4,
    quantityEditable: true,
    labelEditable: true,
    driveTrain: true,
    fields: [
      { ...WEIGHT, label: '单重' },
      { ...PRICE, label: '单价' },
      {
        key: 'cont_current_a',
        label: '持续电流',
        fullLabel: '电调持续电流',
        unit: 'A',
        kind: 'number',
        min: 0,
        step: 5,
        placeholder: '如 40',
      },
      {
        key: 'peak_current_a',
        label: '峰值电流',
        fullLabel: '电调峰值电流',
        unit: 'A',
        kind: 'number',
        min: 0,
        step: 5,
        placeholder: '如 60',
      },
    ],
  },
  {
    kind: 'propeller',
    label: '螺旋桨',
    group: 'power',
    defaultQty: 4,
    quantityEditable: true,
    labelEditable: true,
    driveTrain: true,
    fields: [
      { ...WEIGHT, label: '单重' },
      { ...PRICE, label: '单价' },
      {
        key: 'diameter_in',
        label: '直径',
        fullLabel: '桨叶直径',
        unit: 'in',
        kind: 'number',
        min: 0,
        max: 40,
        step: 0.1,
        placeholder: '如 10',
      },
      {
        key: 'pitch_in',
        label: '螺距',
        fullLabel: '桨叶螺距',
        unit: 'in',
        kind: 'number',
        min: 0,
        max: 20,
        step: 0.1,
        placeholder: '如 4.5',
      },
      {
        key: 'blades',
        label: '桨叶数',
        fullLabel: '桨叶数量',
        unit: '叶',
        kind: 'number',
        min: 1,
        max: 8,
        step: 1,
        placeholder: '默认 3',
      },
    ],
  },
  {
    kind: 'battery',
    label: '电池',
    group: 'energy',
    defaultQty: 1,
    quantityEditable: true,
    labelEditable: true,
    fields: [
      WEIGHT,
      PRICE,
      {
        key: 'capacity_mah',
        label: '容量',
        fullLabel: '电池容量',
        unit: 'mAh',
        kind: 'number',
        min: 0,
        step: 50,
        placeholder: '如 5200',
      },
      {
        key: 'cells',
        label: '电芯数',
        fullLabel: '电池电芯数',
        unit: 'S',
        kind: 'number',
        min: 1,
        max: 14,
        step: 1,
        placeholder: '如 4',
        hint: '标称电压按 3.7 V/片 计算',
      },
      {
        key: 'discharge_c',
        label: '放电倍率',
        fullLabel: '电池放电倍率',
        unit: 'C',
        kind: 'number',
        min: 0,
        step: 5,
        placeholder: '如 30',
      },
    ],
  },
  {
    kind: 'fc',
    label: '飞控',
    group: 'avionics',
    defaultQty: 1,
    quantityEditable: false,
    labelEditable: true,
    fields: [WEIGHT, PRICE],
  },
  {
    kind: 'vtx',
    label: '图传',
    group: 'avionics',
    defaultQty: 1,
    quantityEditable: false,
    labelEditable: true,
    fields: [WEIGHT, PRICE],
  },
  {
    kind: 'rx',
    label: '接收机',
    group: 'avionics',
    defaultQty: 1,
    quantityEditable: false,
    labelEditable: true,
    fields: [WEIGHT, PRICE],
  },
  {
    kind: 'payload',
    label: '载荷',
    group: 'payload',
    defaultQty: 1,
    quantityEditable: true,
    labelEditable: true,
    fields: [WEIGHT, PRICE],
  },
  {
    kind: 'custom',
    label: '自定义部件',
    group: 'custom',
    defaultQty: 1,
    quantityEditable: true,
    labelEditable: true,
    fields: [WEIGHT, PRICE],
  },
];

export function specOf(kind: ComponentKind): ComponentSpec {
  const spec = COMPONENT_SPECS.find((item) => item.kind === kind);
  if (!spec) throw new Error(`未知部件类型：${kind}`);
  return spec;
}

export function fieldOf(kind: ComponentKind, fieldKey: string): FieldSpec | undefined {
  return specOf(kind).fields.find((field) => field.key === fieldKey);
}

/**
 * 缺失提示里展示的完整参数名。
 * 中文标签直接相接（「电机」+「最大推力」），以拉丁字母开头的标签补一个空格（「电机 KV」）。
 */
export function missingLabel(kindLabel: string, field: FieldSpec): string {
  if (field.fullLabel) return field.fullLabel;
  const needsSpace = /^[A-Za-z0-9]/.test(field.label);
  return `${kindLabel}${needsSpace ? ' ' : ''}${field.label}`;
}

/** 新建项目时的默认部件清单（4 轴标准配置） */
export function defaultComponentKinds(): ComponentKind[] {
  return ['frame', 'motor', 'esc', 'propeller', 'battery', 'fc', 'vtx', 'rx', 'payload'];
}
